import datloweBuild from '@wallaby-ui/build';

class AppConfig {
  /**
   * Determines if application is run from a Web or from a NodeJs environment.
   * @returns {boolean}
   */
  static get IS_BROWSER() {
    return Object.prototype.toString.call(global.process) !== '[object process]';
  }

  static get API_ENDPOINT() {
    return process.env.API_ENDPOINT || 'http://localhost';
  }

  /**
   * @returns {string}
   */
  static get HOST_NAME() {
    const hostName = `${this.API_ENDPOINT}:18802`;
    // url, where search service is served, it has to be configured in proxy server (i.e. nginx)
    // return hostName;
    return AppConfig.IS_BROWSER ? '' : hostName;
  }

  /**
   * @returns {string}
   */
  static get WALLABY_CORE_SERVICE_URL() {
    return `${AppConfig.HOST_NAME}/wallabyCoreApi`;
  }

  /**
   * @returns {string}
   */
  static get MERIE_SERVICE_URL() {
    return `${AppConfig.HOST_NAME}/merieApi`;
  }

  /**
   * @returns {string}
   */
  static get KEYCLOAK_SERVICE_URL() {
    return `${AppConfig.HOST_NAME}/token`;
  }

  /**
   * @returns {string}
   */
  static withoutHostName(endpoint) {
    return endpoint.indexOf(AppConfig.HOST_NAME) === 0 ? endpoint.substring(AppConfig.HOST_NAME.length) : endpoint;
  }

  static getConfig() {
    return {
      class: AppConfig,
      service: {
        url: {
          // general
          configDataSet: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config-dataset`,
          coreEnums: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/enums`,

          // # WALLABY CORE
          appIndexFile: '/',
          autoLayout: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/miniscreens`,
          autoLayoutMetadataMethod: 'metadata',
          baseDashboardReportName: 'dashboard',
          breadcrumbObjectPath: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/navigation/objectPath`,
          cases: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/cases`,
          caseDocuments: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/files/all-by-case-id`,
          caseDocumentsPaginated: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/files/by-case-id`,
          caseDocumentsTableConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/case/documentstable`,
          caseProjectPropertiesConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/case/projectproperties`,
          casePropertiesConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/case/properties`,
          caseResultTableConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/case/searchresultstable`,
          caseTimelineMethod: 'timeline',
          caseTimelineTableConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/case/timelinetable`,
          caseTypes: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/case-types`,
          caseHierarchyNode: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/hierarchies/search/findByCaseObjectId`,
          coreTranslations: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/translations`,
          dashboard: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/homepage`,
          dashboardConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/wombat/homepage`,
          documentConceptsConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/document/concepts`,
          documentFiltersConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/document/filters`,
          documentHierarchiesTableConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/document/hierarchiestable`,
          documentLinkedFilesTableConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/document/linkedfilestable`,
          documentOtherFormatsTableConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/document/otherformatstable`,
          documentPreviewReportId: 'preview',
          documentProjectPropertiesConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/document/projectproperties`,
          documentPropertiesConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/document/properties`,
          documentReportsConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/document/reports`,
          documentReportsMethod: 'reports',
          documentResultTableConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/document/searchresultstable`,
          fileAnnotationsMethod: 'annotations',
          fileAttachedFilesMethod: 'attachedFiles',
          fileDownloadMethod: 'download-by-blobid',
          filePdfMethod: 'pdf',
          fileTextChunksMethod: 'textchunks',
          fileTextMethod: 'text',
          fileTypeMethod: 'type',
          files: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/files`,
          filesExportMethod: 'export',
          hierarchies: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/hierarchies`,
          hierarchiesByPathsMethod: 'by-paths',
          hierarchiesByPrefixMethod: 'by-prefix',
          hierarchiesByIdsMethod: 'by-ids',
          hierarchy: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/hierarchy`,
          hierarchyChildren: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/hierarchies/search/findByTypeIdAndParentId`,
          hierarchyChildrenByParent: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/hierarchies/search/findByParentId`,
          hierarchyChildrenByType: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/hierarchies/search/findByTypeId`,
          hierarchyFilter: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/files/search/findByParentHierarchy`,
          hierarchyTypes: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/hierarchy-types`,
          i18nConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/i18n`,
          i18nLocaleMessages: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/i18n`,
          infections: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/infections`,
          infectionsFiltersConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/hai/filters`,
          infectionsPropertiesConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/hai/properties`,
          infectionsResultTableConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/hai/searchresultstable`,
          info: `${AppConfig.WALLABY_CORE_SERVICE_URL}/actuator`,
          instanceConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/instance`,
          keycloak: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/keycloak`,
          snomedLabels: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/resources/snomed-labels`,
          processingStatus: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/application-processing-status`,

          // login
          login: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/login`,
          logout: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/logout`,

          miniScreensConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/miniscreens`,
          navigationConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/navigation`,

          // organization
          organizations: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/organizations/all`,
          organizationsFilterData: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/organizations/filter-data`,
          organizationsFilterDataByOrgSecurity: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/organizations/filter-data-by-org-security`,

          // medications
          medications: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/resources/medications`,
          medicationAtcGroups: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/resources/medications/{id}/atc-hierarchy-groups`,
          medicationAtcGroupsByMedicationId: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/resources/medications/{id}/atc-hierarchy-groups`,
          medicationsFilterData: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/atc-hierarchy/filter-data`,

          // patient
          patientsRoot: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/patients`,
          patients: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/patients/page`,
          patientsFiltersConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/patient/filters`,
          patientsPropertiesConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/patient/properties`,
          patientsResultTableConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/patient/searchresultstable`,

          // encounters
          documents: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/documents/encounter/`,
          chartRoot: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/lab-results`,
          // microbiology filter of the hospitalization list (PDM-2748): pathogen catalogue and tested antibiotics
          labResultFindingCodes: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/lab-result-finding-codes/all`,
          labsAtbList: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/lab-results/atb-list`,
          chart: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/lab-results/encounter`,

          projectConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/project`,
          propertyTypes: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/property-types`,
          range: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/range`,
          search: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/search`,
          security: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/security`,
          securedHierarchiesByPathsMethod: 'secured-by-paths',
          securedHierarchiesByPrefixMethod: 'secured-by-prefix',
          securedHierarchyChildrenByParent: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/hierarchies/securedFindByParentId`,
          securedHierarchyChildrenByType: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/hierarchies/securedFindByTypeId`,
          token: `${AppConfig.KEYCLOAK_SERVICE_URL}`,
          translate: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/translate/translate`,
          translateIssueToken: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/translate/issueToken`,
          upload: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/files`,
          userFilters: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/user-filters`,

          // # MERIE
          // general
          permissions: `${AppConfig.MERIE_SERVICE_URL}/api/permissions`,
          merieEnums: `${AppConfig.MERIE_SERVICE_URL}/api/enums`,
          merieEnumsHierarchical: `${AppConfig.MERIE_SERVICE_URL}/api/enums/hierarchical`,
          merieTranslations: `${AppConfig.MERIE_SERVICE_URL}/api/translations`,
          userGuidePdf: `${AppConfig.MERIE_SERVICE_URL}/api/user-guide/pdf`,

          // hai
          haiDashboardService: `${AppConfig.MERIE_SERVICE_URL}/api/hai/dashboard`,
          haiList: `${AppConfig.MERIE_SERVICE_URL}/api/hai/discovery/page`,

          // patients
          haiPatients: `${AppConfig.MERIE_SERVICE_URL}/api/patients/`,
          hospitalizations: `${AppConfig.MERIE_SERVICE_URL}/api/evaluated-encounters/hospitalizations`,
          hospitalizationsExport: `${AppConfig.MERIE_SERVICE_URL}/api/evaluated-encounters/hospitalizations/xlsx`,
          hospitalizationRiskFactor: `${AppConfig.MERIE_SERVICE_URL}/api/evaluated-encounters`,
          hospitalizationRiskFactorToggleActivated: `${AppConfig.MERIE_SERVICE_URL}/api/evaluated-encounters/{id}/risk-factors/{riskFactorId}/state`,
          hospitalizationsIdFromSrcId: `${AppConfig.MERIE_SERVICE_URL}/api/evaluated-encounters/id-from-srcid/{srcId}`,
          hospitalizationChartEvents: `${AppConfig.MERIE_SERVICE_URL}/api/evaluated-encounters/{id}/timeline/chart-events`,

          // filters
          numberRange: `${AppConfig.MERIE_SERVICE_URL}/api/evaluated-encounters/properties`,
          // ICD codes of structured diagnoses attributed to hospitalizations (PDM-2749)
          icdList: `${AppConfig.MERIE_SERVICE_URL}/api/evaluated-encounters/icd-codes`,

          // instance params
          instancePublishEnabled: `${AppConfig.MERIE_SERVICE_URL}/api/instance/publish-enabled`,
          instanceLocation: `${AppConfig.MERIE_SERVICE_URL}/api/instance/location`,
          maxExportRows: `${AppConfig.MERIE_SERVICE_URL}/api/instance/max-export-rows`,

          // hospitalizations detail
          hospitalizationDetail: `${AppConfig.MERIE_SERVICE_URL}/api/evaluated-encounters`,
          hospitalizationDetailDocumentVersionMethod: 'document-versions',
          hospitalizationPhRecommendationListGet: `${AppConfig.MERIE_SERVICE_URL}/api/evaluated-encounters/{evaluatedEncounterId}/pharmacotherapy-recommendations`,
          hospitalizationPhRecommendationCreate: `${AppConfig.MERIE_SERVICE_URL}/api/evaluated-encounters/{evaluatedEncounterId}/pharmacotherapy-recommendations`,
          hospitalizationPhRecommendationDelete: `${AppConfig.MERIE_SERVICE_URL}/api/evaluated-encounters/{evaluatedEncounterId}/pharmacotherapy-recommendations/{pharmaRecId}`,
          hospitalizationPhRecommendationEdit: `${AppConfig.MERIE_SERVICE_URL}/api/evaluated-encounters/{evaluatedEncounterId}/pharmacotherapy-recommendations/{pharmaRecId}`,
          hospitalizationPhRecommendationPublish: `${AppConfig.MERIE_SERVICE_URL}/api/evaluated-encounters/{evaluatedEncounterId}/pharmacotherapy-recommendations/{pharmaRecId}/publish`,
          hospitalizationPhRecommendationExportText: `${AppConfig.MERIE_SERVICE_URL}/api/pharmacotherapy-recommendations/text-export`,
          hospitalizationPhRecommendationNonReimbursableProcedures: `${AppConfig.MERIE_SERVICE_URL}/api/evaluated-encounters/{evaluatedEncounterId}/pharmacotherapy-recommendations/non-reimbursable-procedures`,

          // risk factors
          hospitalizationRiskFactors: `${AppConfig.MERIE_SERVICE_URL}/api/evaluated-encounters/{evaluatedEncounterId}/risk-factors/available-types-for-creation`,
          riskFactorLabelMapping: `${AppConfig.MERIE_SERVICE_URL}/api/risk-factors/risk-factor-label-mapping`,

          // saved filters
          userFilterDelete: `${AppConfig.MERIE_SERVICE_URL}/api/user-filter/{userFilterId}`,
          userFilterGet: `${AppConfig.MERIE_SERVICE_URL}/api/user-filter/all`,
          userFilterPost: `${AppConfig.MERIE_SERVICE_URL}/api/user-filter`,
          defaultUserFilterPost: `${AppConfig.MERIE_SERVICE_URL}/api/user-filter/component/{componentName}/{userFilterId}?default={default}`,
          userFilterShare: `${AppConfig.MERIE_SERVICE_URL}/api/user-filter/{userFilterId}/share`,
          userFilterRevertToPrivate: `${AppConfig.MERIE_SERVICE_URL}/api/user-filter/{userFilterId}/revert-to-private`,
          userFilterDeleteShared: `${AppConfig.MERIE_SERVICE_URL}/api/user-filter/shared/{userFilterId}`,

          // Pharmacotherapy
          recommendations: `${AppConfig.MERIE_SERVICE_URL}/api/pharmacotherapy-recommendations`,
          recommendationsExport: `${AppConfig.MERIE_SERVICE_URL}/api/pharmacotherapy-recommendations/xlsx`,

          // Reports
          interventionStatisticsReport: `${AppConfig.MERIE_SERVICE_URL}/api/pharmacotherapy-recommendations/reports/overview`,
          interventionStatisticsExport: `${AppConfig.MERIE_SERVICE_URL}/api/pharmacotherapy-recommendations/reports/overview/xlsx`,
          interventionDepartmentReport: `${AppConfig.MERIE_SERVICE_URL}/api/pharmacotherapy-recommendations/reports/department`,
          interventionDepartmentExport: `${AppConfig.MERIE_SERVICE_URL}/api/pharmacotherapy-recommendations/reports/department/xlsx`,
          uzisReport: `${AppConfig.MERIE_SERVICE_URL}/api/uzis-reports`,
          uzisReportExport: `${AppConfig.MERIE_SERVICE_URL}/api/uzis-reports/xlsx`,

          // Combinations
          combinationsInstancesList: `${AppConfig.MERIE_SERVICE_URL}/api/combinations/instances`,
          combinationsInstanceCreate: `${AppConfig.MERIE_SERVICE_URL}/api/combinations/instances`,
          combinationsInstanceGet: `${AppConfig.MERIE_SERVICE_URL}/api/combinations/instances/{id}`,
          combinationsInstanceUpdate: `${AppConfig.MERIE_SERVICE_URL}/api/combinations/instances/{id}`,
          combinationsInstanceDelete: `${AppConfig.MERIE_SERVICE_URL}/api/combinations/instances/{id}`,
          combinationsInstanceValues: `${AppConfig.MERIE_SERVICE_URL}/api/combinations/instances/{id}/values`,
          combinationsInstanceAddValues: `${AppConfig.MERIE_SERVICE_URL}/api/combinations/instances/{id}/values`,
          combinationsValueGet: `${AppConfig.MERIE_SERVICE_URL}/api/combinations/values/{dimension}/{valueId}`,
          combinationsValueUpdate: `${AppConfig.MERIE_SERVICE_URL}/api/combinations/values/{dimension}/{valueId}`,
          combinationsValueDelete: `${AppConfig.MERIE_SERVICE_URL}/api/combinations/values/{dimension}/{valueId}`,
          combinationsShortcutsList: `${AppConfig.MERIE_SERVICE_URL}/api/combinations/shortcuts`,
          combinationsShortcutsReplace: `${AppConfig.MERIE_SERVICE_URL}/api/combinations/shortcuts`,
        },
      },
    };
  }
}

export default AppConfig.getConfig();
